import type { QrDecoder } from './linkedin-qr';
import type { QrDecodeWorkerRequest, QrDecodeWorkerResponse } from './qr-decode.worker';

/**
 * Client-side QR decode with zxing-wasm — U5 / Key Technical Decision 4 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md.
 *
 * Reads the **original `File`/`Blob` bytes**, never a display thumbnail: the
 * prototype's DEV NOTE (docs/lead-scanner-prototype.html ~lines 813-851) is
 * explicit that "down-scaling can destroy the QR module pattern". zxing's
 * `readBarcodes` accepts a `Blob` directly, so the original `File` from the
 * picker goes in untouched — there is no intermediate canvas/resize step to
 * get wrong.
 *
 * This module only READS the code; it does not decide what a payload means.
 * The LinkedIn shape validation lives in `linkedin-qr.ts` and is applied by
 * `photo-intake.ts` (see that file's `QrDecoder` doc for why the split).
 *
 * ─── Two entry points, two loaders (Part 2 fix: the main-thread freeze) ───
 * `decodeQrFromPhoto` (still-photo, U5) used to run `readBarcodes` inline on
 * the main thread — on a real phone photo that pegs the main thread for a
 * meaningful chunk of time, and the photo-intake pipeline runs it for up to
 * `PHOTO_RESOLVE_CONCURRENCY` photos concurrently (`photo-intake.ts`), which
 * froze touch/scroll input during scanning (the OS can't dispatch touch
 * events while the main thread is pegged). It now delegates to
 * `qr-decode.worker.ts`, a dedicated Web Worker that owns its OWN
 * `loadReader()`/WASM-loading code — see that file's header.
 *
 * `decodeQrFromImageData` (live-camera, U12) stays main-thread-only and
 * keeps its OWN duplicate, lightweight loader below, deliberately NOT
 * entangled with the worker: the live-camera loop already has its own
 * tick-drops-mid-decode backpressure (`camera-qr.ts`'s `decoding` flag)
 * rather than a message-queue's, and a worker cannot see another execution
 * context's module cache anyway, so there is no state to actually
 * de-duplicate between the two loaders — only two independent memoization
 * caches with the same shape. This path is explicitly lower priority and out
 * of primary scope for the Part 2 fix; duplicating rather than entangling is
 * what keeps it working unchanged.
 *
 * ─── Why the live-camera loader is still dynamically imported ─────────────
 * Two reasons, both real:
 *  1. Bundle: the WASM glue is ~hundreds of KB and only ever runs after a rep
 *     has opened the live-camera scanner. A dynamic import lets Vite split it
 *     out of the initial chunk, which matters on an event-floor phone.
 *  2. Programs: the `?url` asset import below is a Vite-only specifier that
 *     resolves under `tsconfig.miniapps.json` (Bundler resolution +
 *     `types: ["vite/client"]`) and nowhere else. Keeping it inside the
 *     function body means no other tsconfig program — and no `bun test` run —
 *     ever has to resolve it.
 */

/** `readBarcodes` uses the Emscripten module's `locateFile` hook to find its
 *  `.wasm`, defaulting to a jsDelivr CDN URL baked in at zxing's build time.
 *  We override it to a Vite-emitted asset on our own origin instead: an
 *  event-floor app must not depend on a third-party CDN being reachable, and
 *  a runtime fetch of executable WASM from an origin we don't control is not
 *  a dependency worth taking. `?url` makes Vite copy the binary into the
 *  build output and hand back its hashed URL. */
async function importReader() {
  const [reader, wasmUrl] = await Promise.all([
    import('zxing-wasm/reader'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);

  reader.prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? wasmUrl.default : prefix + path,
    },
  });

  return reader;
}

/** Memoized so a batch of live-camera frames imports the module and
 *  registers the `locateFile` override ONCE — zxing's own override cache is
 *  keyed by a shallow-equality check on the overrides object, and a fresh
 *  `locateFile` closure per call would never compare equal, forcing a
 *  re-instantiation per frame. Cleared on rejection so a transient
 *  chunk-load failure isn't cached as permanent. */
let readerPromise: ReturnType<typeof importReader> | null = null;

function loadReader() {
  if (readerPromise === null) {
    readerPromise = importReader();
    readerPromise.catch(() => {
      readerPromise = null;
    });
  }
  return readerPromise;
}

/**
 * The zxing reader options — identical to `qr-decode.worker.ts`'s own copy
 * (used by `decodeQrFromPhoto` there) on purpose: a viewfinder frame of a
 * badge is the same hard problem as a still of one — angled, glare-lit,
 * small — so the accuracy-over-speed trade is the right one in both places.
 * The live loop's budget for a slow `tryHarder` pass comes from its interval
 * (`camera-qr.ts` drops ticks that land mid-decode) rather than from cheaper
 * decode options.
 */
const QR_READER_OPTIONS: {
  // Spelled out rather than `as const` (which infers a readonly tuple
  // zxing's mutable `formats?: ReadInputBarcodeFormat[]` won't accept) and
  // rather than importing zxing's own type — everything zxing here is
  // dynamically imported, for the two reasons in this file's header.
  formats: ('QRCode' | 'MicroQRCode' | 'RMQRCode')[];
  tryHarder: boolean;
  tryRotate: boolean;
  tryInvert: boolean;
} = {
  // QR only. A badge's Code128/EAN barcode is a registration-system
  // artifact with no identity in it, and reading formats we will never
  // act on is wasted time per photo.
  formats: ['QRCode', 'MicroQRCode', 'RMQRCode'],
  // Angled shots and screen glare are the known weak point (prototype
  // DEV NOTE) — accuracy over speed is the right trade for a rep who
  // gets one still capture and no viewfinder feedback loop.
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
};

/**
 * The live-camera counterpart (U12 / R12 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md): decodes one viewfinder
 * frame already rasterized to `ImageData`, returning the raw payload or
 * `null`.
 *
 * `readBarcodes` accepts `ImageData` directly, so a frame the component has
 * painted into its offscreen canvas needs no re-encode to a Blob — which
 * matters when this runs several times a second. Stays on the main thread
 * (out of scope for the Part 2 worker fix — see this file's header) with its
 * own `loadReader()` above.
 *
 * Never throws: a missing WASM binary, an unsupported image container, or a
 * corrupt frame are all "keep looking" from the viewfinder's point of view —
 * and it matters more here than for the still-photo path, since a viewfinder
 * produces a stream of unreadable frames by definition (mid-focus,
 * motion-blurred, nothing in view), and every one of them is "keep looking",
 * not an error to surface to the rep.
 */
export async function decodeQrFromImageData(imageData: ImageData): Promise<string | null> {
  try {
    const { readBarcodes } = await loadReader();
    const results = await readBarcodes(imageData, QR_READER_OPTIONS);

    const decoded = results.find((r) => r.isValid && r.text.length > 0);
    return decoded?.text ?? null;
  } catch {
    return null;
  }
}

// ─── Still-photo path — delegates to qr-decode.worker.ts ──────────────────

/** One shared `Worker` instance for the lifetime of the page — mirrors the
 *  main-thread loader's `readerPromise` memoization above: a 14-photo batch
 *  spins this up once, not fourteen times. `null` until the first
 *  `decodeQrFromPhoto` call (lazy, same reasoning as the dynamic
 *  `importReader` import: no reason to pay for the worker script fetch/parse
 *  before a rep has actually picked a photo). */
let worker: Worker | null = null;
let nextRequestId = 0;
const pendingRequests = new Map<number, (result: string | null) => void>();

function getWorker(): Worker {
  if (worker !== null) return worker;

  const instance = new Worker(new URL('./qr-decode.worker.ts', import.meta.url), { type: 'module' });
  instance.onmessage = (event: MessageEvent<QrDecodeWorkerResponse>) => {
    const { id, result } = event.data;
    const resolve = pendingRequests.get(id);
    if (!resolve) return;
    pendingRequests.delete(id);
    resolve(result);
  };
  instance.onerror = () => {
    // The worker script itself failed to load/run (as opposed to a decode
    // inside it failing, which the worker already answers as `result:
    // null`, never as an `onerror`). Resolve every outstanding request to
    // `null` — this function's never-throws contract, restated below,
    // applies here too — and drop the instance so the NEXT call spins up a
    // fresh one rather than staying permanently wedged.
    for (const resolve of pendingRequests.values()) resolve(null);
    pendingRequests.clear();
    worker = null;
  };
  worker = instance;
  return instance;
}

/**
 * Decodes the first readable QR code in a photo and returns its raw payload,
 * or `null` when there is nothing to read.
 *
 * Runs the actual decode inside `qr-decode.worker.ts` (Part 2 fix — see this
 * file's header) rather than inline; this function is the message/promise
 * wrapper around that worker.
 *
 * Never throws: a missing WASM binary, an unsupported image container (HEIC
 * on a browser that can't decode it), a corrupt file, or the worker itself
 * failing to load are all "no QR here" from the pipeline's point of view,
 * and the pipeline's fall-through to vision OCR is the correct handling for
 * every one of them. The pipeline catches too — this is belt-and-braces, not
 * redundancy for its own sake: callers of a `QrDecoder` shouldn't have to
 * know which failures are recoverable.
 */
export const decodeQrFromPhoto: QrDecoder = (file) =>
  new Promise((resolve) => {
    try {
      const id = nextRequestId++;
      pendingRequests.set(id, resolve);
      getWorker().postMessage({ id, file } satisfies QrDecodeWorkerRequest);
    } catch {
      resolve(null);
    }
  });
